import React, { useCallback, useContext, useState } from "react";
import apiClient from "../../utils/apiClient";
import MaterialViewerModal, { isViewableType } from "./MaterialViewerModal";
import MaterialViewerContext from "./materialViewerContext";
import MaterialVideoModal from "./MaterialVideoModal";
import { canDownloadFile } from "../ContentProtection/contentAccess";

/**
 * useMaterialViewer — shared in-app document viewing for materials.
 *
 * Materials (study materials, video-linked materials, syllabus-linked materials,
 * smart-session documents) are private files in R2. This hook opens the file
 * INSIDE the LMS using MaterialViewerModal (→ ContentViewer: PDF / DOCX / PPT /
 * XLS / image / text are all rendered inline).
 *
 * Usage:
 *   const { openMaterial, downloadMaterial, viewer } = useMaterialViewer();
 *   ...
 *   <button onClick={() => openMaterial(mat)}>View</button>
 *   {viewer}   // render once; it's null until a material is opened
 *
 * `mat` only needs `{ _id, title, fileType, fileName? }`. The URL — and what the
 * student may do with the file — is fetched on demand via GET /materials/:id/view.
 * A view-only file's `access.canDownload` is false; the modal then renders it
 * from memory and offers no download or open-in-new-tab. A video material plays
 * in MaterialVideoModal instead — students never download a video.
 */
const VIDEO_TYPES = ["MP4", "MOV", "WEBM", "MKV"];
const isVideoMaterial = (mat) => !!mat?.isVideo || VIDEO_TYPES.includes(String(mat?.fileType || "").toUpperCase());

export default function useMaterialViewer() {
  const Viewer = useContext(MaterialViewerContext) || MaterialViewerModal;
  const [material, setMaterial] = useState(null);
  const [view, setView] = useState(null);
  const [show, setShow] = useState(false);
  // A video material streams in a player; it has no document view and no download.
  const [video, setVideo] = useState(null);

  const close = useCallback(() => {
    setShow(false);
    setMaterial(null);
    setView(null);
  }, []);

  // Open the in-app viewer for a material.
  const openMaterial = useCallback(async (mat) => {
    if (!mat?._id) return;
    if (isVideoMaterial(mat)) {
      setVideo(mat);
      return;
    }
    setMaterial(mat);
    setView(null);
    setShow(true);
    try {
      const { data } = await apiClient.get(`/materials/${mat._id}/view`);
      const next = data?.data;
      if (next?.url) setView(next);
      else close();
    } catch {
      close();
    }
  }, [close]);

  // Explicit download-to-device (the modal's Download button, and non-viewable
  // types such as archives). A view-only file opens in the viewer instead, which
  // explains why it cannot be downloaded rather than doing nothing.
  const downloadMaterial = useCallback(async (mat) => {
    if (!mat?._id) return;
    if (!canDownloadFile(mat) || isVideoMaterial(mat)) {
      openMaterial(mat);
      return;
    }
    try {
      const { data } = await apiClient.get(`/materials/${mat._id}/download`);
      const res = data?.data;
      if (res?.url && res.downloadable !== false) window.open(res.url, "_blank", "noopener");
    } catch {
      /* non-fatal */
    }
  }, [openMaterial]);

  const viewer = (
    <>
      <Viewer
        show={show}
        material={material ? { ...material, ...(view ? { access: view.access } : {}) } : null}
        url={view?.url || null}
        pdfUrl={view?.pdfUrl || null}
        onClose={close}
        onDownload={downloadMaterial}
      />
      <MaterialVideoModal material={video} onClose={() => setVideo(null)} />
    </>
  );

  return { openMaterial, downloadMaterial, viewer };
}

// Re-export so callers can decide view-vs-download without importing the modal too.
export { isViewableType };
